import debounce from 'lodash/debounce'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {pipelinesApi} from '../../../services/pipelinesApi'
import {connectionId} from '../../CreatePipelinePage.consts'

import {
  branchNameDefault,
  defaultBranches,
  matchGitHosts,
  repositoryBranchSpecification,
} from './useOutsideRepository.consts'
import {getMatchHosts} from './useOutsideRepository.utils'

const THROTTLE_TIME = 500

function useOutsideRepository(searchValue: string, username?: string) {
  const [isRepositoryTestConnectionLoading, setRepositoryTestConnectionLoading] =
    useState<boolean>(false)
  const isUrlVerifiedRepository = getMatchHosts(matchGitHosts, searchValue)
  const [
    fetchRepositoryTestConnection,
    {
      isRepositoryTestConnection,
      isRepositoryTestConnectionError,
      isRepositoryTestConnectionFetching,
    },
  ] = pipelinesApi.endpoints.repositoryTestConnection.useLazyQuery({
    selectFromResult: ({data, error, isFetching, isLoading}) => ({
      isRepositoryTestConnection: data?.status === 'OK',
      isRepositoryTestConnectionError: !!error || data?.status === 'ERROR',
      isRepositoryTestConnectionFetching: isFetching || isLoading,
    }),
  })
  const [fetchBranches, {isRepositoryBranchesFetching}] =
    pipelinesApi.endpoints.getRepositoryBranches.useLazyQuery({
      selectFromResult: ({isFetching, isLoading}) => ({
        isRepositoryBranchesFetching: isFetching || isLoading,
      }),
    })
  const fetchRepositoryTestConnectionThrottle = useMemo(
    () =>
      debounce(
        props =>
          fetchRepositoryTestConnection(props).finally(() =>
            setRepositoryTestConnectionLoading(false),
          ),
        THROTTLE_TIME,
        {leading: false, trailing: true},
      ),
    [fetchRepositoryTestConnection],
  )
  const getRepositoryDefaultBranch = useCallback(
    async (url: string) => {
      try {
        const {data} = await fetchBranches({
          url,
          branchSpecification: repositoryBranchSpecification,
          connectionId,
          username,
        })

        return data?.find(branch => defaultBranches.has(branch)) ?? branchNameDefault
      } catch (e) {
        return branchNameDefault
      }
    },
    [fetchBranches, username],
  )

  useEffect(() => {
    if (isUrlVerifiedRepository && !isRepositoryTestConnectionFetching) {
      fetchRepositoryTestConnectionThrottle({
        url: searchValue.toString().trim(),
        isPrivate: true,
        connectionId,
        username,
      })

      setRepositoryTestConnectionLoading(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  return {
    getRepositoryDefaultBranch,
    isRepositoryTestConnection,
    isUrlVerifiedRepository,
    isRepositoryBranchesFetching,
    isRepositoryTestConnectionLoading,
    isRepositoryTestConnectionError,
  }
}

export default useOutsideRepository
